import type { FunctionComponent, SetStateAction } from 'react';
import { useState } from 'react';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../../types';
import Button, { BUTTON_TYPE } from '../../../../../../generic/Button';
import { SidebarLayout } from '../../../../../../layouts';
import { FilterPanel, FormLabelVariant, LabeledSelect } from '../../../../../../generic';
import type { SelectOption, SetState } from '../../../../../../../types';
import styles from './Filter.module.less';
import IncludeCheckboxGroup from './IncludeCheckboxGroup';
import DependencyMetadataCheckboxGroup from './DependencyMetadataCheckboxGroup';
import type { DependencyInfoOption, ReportCategory } from '../../../../shared/types';
import RegulatoryComplianceToggles from './RegulatoryComplianceToggles';
import { DependencyInfoOptions, ReportOptionDetails } from '../../../../ReportGeneration/utils';
import { licenseIncludeOptions } from '../../constants';
import PaidFeatureGate from '../../../../../../generic/Gates/PaidFeatureGate';
import { isFreeTier } from '../../../../../../../../shared/subscriptions';
import { window } from '../../../../../../../globals';

export type FormatSelection = SelectOption<FormatName>;

type FormatSelectProps = {
  format: FormatSelection;
  formats: FormatSelection[];
  setFormat: SetState<FormatSelection>;
};

const FormatSelect: FunctionComponent<FormatSelectProps> = ({ format, formats, setFormat }: FormatSelectProps) => (
  <div className="width-full" data-test-id="report-filters">
    <LabeledSelect
      variant={FormLabelVariant.Filter}
      label="Format"
      value={format}
      options={formats}
      id="report-format"
      onChange={(option: FormatSelection | null) => {
        if (option) {
          setFormat(option);
        }
      }}
    />
  </div>
);

type FilterState = {
  format: FormatName;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyInfoOption[];
};

export type OnApply = (args: FilterState) => void;

type Props = {
  category: ReportCategory;
  format: FormatSelection;
  setFormat: SetState<FormatSelection>;
  formats: FormatSelection[];
  onApply: OnApply;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  setIsDirty: SetState<boolean>;
  isApplyButtonDisabled: boolean;
};

const Filter = ({
  category,
  format,
  setFormat,
  formats,
  onApply,
  setIsDirty,
  isApplyButtonDisabled,
  includeOptions: appliedIncludeOptions,
  dependencyMetadataOptions: appliedDependencyMetadataOptions,
}: Props) => {
  const isPaidFeatureGateRestricted = isFreeTier(window?.user?.organization?.stripe_plan);
  const [flatIncludeValues, setFlatIncludeValues] = useState<SelectOption[]>(
    appliedIncludeOptions
      .filter((option) => !licenseIncludeOptions.has(option))
      .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title })),
  );
  const [includeCheckboxLicenseValues, setIncludeCheckboxLicenseValues] = useState<SelectOption[]>(
    appliedIncludeOptions
      .filter((option) => licenseIncludeOptions.has(option))
      .map((option) => ({ value: option, label: ReportOptionDetails[option]?.title })),
  );
  const [dependencyMetadataCheckboxValues, setDependencyMetadataCheckboxValues] = useState<SelectOption[]>(
    appliedDependencyMetadataOptions.map((option) => ({ value: option, label: DependencyInfoOptions[option] })),
  );
  // Add a helper function to wrap any set function to set isDirty to true
  function withSetIsDirty<T>(fn: (value: SetStateAction<T>) => void) {
    return (value: SetStateAction<T>) => {
      setIsDirty(true);
      fn(value);
    };
  }
  const includeOptions = [...flatIncludeValues, ...includeCheckboxLicenseValues].map(
    ({ value }) => value as IncludeFilterOption,
  );
  const dependencyMetadataOptions = dependencyMetadataCheckboxValues.map(({ value }) => value as DependencyInfoOption);
  const onClick = () => {
    setIsDirty(false);
    onApply({ format: format.value, includeOptions, dependencyMetadataOptions });
  };
  const title = (
    <div className={styles.filterTitle}>
      <span>Customization</span>
      <Button type={BUTTON_TYPE.SECONDARY} onClick={onClick} isDisabled={isApplyButtonDisabled}>
        Apply
      </Button>
    </div>
  );
  return (
    <SidebarLayout.Sidebar>
      <FilterPanel title={title}>
        <FormatSelect format={format} formats={formats} setFormat={withSetIsDirty(setFormat)} />
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
          <RegulatoryComplianceToggles
            setFlatIncludeValues={withSetIsDirty(setFlatIncludeValues)}
            setIncludeCheckboxLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
            setDependencyMetadataCheckboxValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
            flatIncludeValues={flatIncludeValues}
            includeCheckboxLicenseValues={includeCheckboxLicenseValues}
            dependencyMetadataCheckboxValues={dependencyMetadataCheckboxValues}
          />
        </PaidFeatureGate>
        <IncludeCheckboxGroup
          format={format}
          category={category}
          values={flatIncludeValues}
          setValues={withSetIsDirty(setFlatIncludeValues)}
          licenseValues={includeCheckboxLicenseValues}
          setLicenseValues={withSetIsDirty(setIncludeCheckboxLicenseValues)}
        />
        <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
          <DependencyMetadataCheckboxGroup
            values={dependencyMetadataCheckboxValues}
            setValues={withSetIsDirty(setDependencyMetadataCheckboxValues)}
          />
        </PaidFeatureGate>
      </FilterPanel>
    </SidebarLayout.Sidebar>
  );
};

export default Filter;
